import { Component, createElement } from "react";
import PropTypes from "prop-types";

class ViewportObserver extends Component {
    observer = null;
    element = null;
    isIntersected = false;

    static propTypes = {
        children: PropTypes.node.isRequired,
        disconnectOnEnter: PropTypes.bool,
        disconnectOnLeave: PropTypes.bool,
        onChange: PropTypes.func,
        onEnter: PropTypes.func,
        onLeave: PropTypes.func,
        root: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
        rootMargin: PropTypes.string,
        tagName: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        threshold: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
    };

    static defaultProps = {
        disconnectOnEnter: false,
        disconnectOnLeave: false,
        onChange: null,
        onEnter: null,
        onLeave: null,
        root: null,
        rootMargin: "0px",
        tagName: "div",
        threshold: 0,
    };

    componentDidMount() {
        const { root, rootMargin, threshold } = this.props;

        this.observer = new IntersectionObserver(this.handleIntersectionCallback, { root, rootMargin, threshold });

        if (this.element && this.observer && this.observer.observe) {
            this.observer.observe(this.element);
        }
    }

    componentWillUnmount() {
        this.removeObserver();
    }

    handleIntersectionCallback = (entries) => {
        if (entries.length === 0) {
            return;
        }

        const { onEnter, onLeave, onChange, disconnectOnEnter, disconnectOnLeave } = this.props;

        entries.forEach((entry) => {
            const inViewport = entry.intersectionRatio > 0;

            if (onEnter && !this.isIntersected && inViewport) {
                onEnter(this.element);

                if (disconnectOnEnter) {
                    this.removeObserver();
                }
            }

            if (onChange) {
                onChange(entry);
            }

            if (onLeave && this.isIntersected && !inViewport) {
                onLeave();

                if (disconnectOnLeave) {
                    this.removeObserver();
                }
            }

            this.isIntersected = inViewport;
        });
    };

    removeObserver() {
        if (this.observer) {
            this.observer.unobserve(this.element);
            this.observer.disconnect();
            this.observer = null;
        }
    }

    setElement = (element) => {
        this.element = element;
    };

    render() {
        const {
            tagName,
            onEnter,
            onChange,
            onLeave,
            root,
            rootMargin,
            threshold,
            disconnectOnEnter,
            disconnectOnLeave,
            children,
            ...props
        } = this.props;

        const selfRef = typeof tagName === "string" ? "ref" : "innerRef";

        return createElement(tagName, { ...props, [selfRef]: this.setElement }, children);
    }
}

export default ViewportObserver;
